import { PaginatedResponse, useApi } from '@/api';
import { Link, TableCellBottom, getBorrowersEmptyBoxText } from '@/components';
import { DEFAULT_NETWORK } from '@/constants';
import {
  CurrencyValue,
  DataTable,
  EmptyBox,
  PercentageValue,
  RelativeDateTime,
  TableSkeleton,
  WalletAddress,
  useTableProps
} from '@blockanalitica/ui';
import { TablePagination } from '@blockanalitica/ui/package/types/src/components/atoms/Table/types';
import {
  TableColumn,
  TableData
} from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';

type MarketWalletsBorrowersTableProps = {
  pagination: TablePagination;
  textSearch: string | null;
  network: string;
  marketAddress: string;
  tag: string;
  symbol: string;
};

type WalletData = {
  wallet_address: string;
  underlying_symbol: string;
  supply: string;
  supply_usd: string;
  borrow: string;
  borrow_usd: string;
  datetime: string;
  supply_change_1d: string | null;
  supply_change_1d_usd: string | null;
  borrow_change_1d: string | null;
  borrow_change_1d_usd: string | null;
  last_activity: string;
  borrow_share: string | null;
  supply_share: string | null;
};

export default function MarketWalletsBorrowersTable({
  textSearch,
  marketAddress,
  pagination,
  tag,
  symbol
}: MarketWalletsBorrowersTableProps) {
  const network = DEFAULT_NETWORK;
  const { sorting } = useTableProps({
    pageSize: 10,
    filtersDebounceTime: 500,
    initialSorting: '-borrow',
    initialFilters: {
      textSearch: ''
    }
  });
  const [data, error] = useApi<PaginatedResponse<WalletData[]>>({
    url: `/markets/${marketAddress}/wallets/`,
    queryParams: {
      page: pagination.currentPage,
      limit: pagination.pageSize,
      sort: sorting.currentSorting,
      wallet_address: textSearch,
      type: 'borrowers'
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton columns={6} rows={10} />;
  }
  const columns: TableColumn[] = [
    {
      header: '',
      getCell: (row) => <WalletAddress address={row.wallet_address} />
    },
    {
      header: 'Borrow',
      getCell: (row) => (
        <CurrencyValue
          value={row.borrow}
          options={{
            currencySize: '-1',
            currency: {
              name: row.underlying_symbol,
              address: marketAddress,
              network: network
            }
          }}
        />
      ),
      getCellBottom: (row) => (
        <TableCellBottom checkValue={row.borrow_usd}>
          <CurrencyValue
            value={row.borrow_usd}
            options={{
              currency: 'USD'
            }}
          />
        </TableCellBottom>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'borrow'
    },
    {
      header: '1D Change',
      getCell: (row) => (
        <CurrencyValue
          value={row.borrow_change_1d}
          options={{
            currency: {
              name: row.underlying_symbol,
              address: marketAddress,
              network: network
            },
            trend: true,
            currencySize: '-1'
          }}
        />
      ),
      getCellBottom: (row) => (
        <TableCellBottom checkValue={row.borrow_change_1d_usd}>
          <CurrencyValue
            value={row.borrow_change_1d_usd}
            options={{
              currency: 'USD',
              absolute: true
            }}
          />
        </TableCellBottom>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'borrow_change_1d'
    },
    {
      header: 'Borrow Share',
      getCell: (row) => <PercentageValue value={row.borrow_share} />,
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'borrow_share'
    },
    {
      header: 'Last Activity',
      getCell: (row) => <RelativeDateTime value={row.last_activity} />,
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'last_activity'
    }
  ];

  return data.pagination.total > 0 ? (
    <DataTable
      rows={data.results}
      columns={columns}
      variant="insideCard"
      options={{
        variants: {
          headerCell: 'insideCard',
          tableCell: 'insideCard'
        }
      }}
      getKey={(row) => `${row.wallet_address}`}
      rowLinkProps={{
        to: (row: TableData) => `${row.wallet_address}`,
        // @ts-ignore
        linkComponent: Link
      }}
      sorting={sorting}
      pagination={{
        ...pagination,
        totalRecords: data.pagination.total
      }}
    />
  ) : (
    <EmptyBox message={getBorrowersEmptyBoxText(tag, symbol)} />
  );
}
